import type { CollectionStrings } from "../i18n/collection-strings";

/**
 * Triggering a site rebuild after a content edit.
 *
 * `apps/one-ai` prerenders its CMS records as pages, so a saved edit is invisible
 * until the site is rebuilt. Nothing in git changes when an editor saves — the
 * record lives in Firestore — so Cloudflare Pages' git integration never fires.
 * These deploy hooks are the only thing that closes that loop.
 *
 * **`apps/onelive` and `apps/chichat` deliberately have no hook here.** Their
 * CMS sections re-read Firestore on mount (`useLiveCollection`), so publishing
 * there needs no deploy at all. That includes the home NEWS, which reads the
 * news stream filtered by `product`. A news save still rebuilds one-ai, and
 * that rebuild is what gives a product site's new row a page to link to.
 *
 * See phase 6 of `apps/one-ai/src/spec/publish-and-rebuild.md`.
 */

/**
 * One rebuildable environment. The keys are the Cloudflare Pages branches the
 * hooks are created against — a deploy hook is branch-scoped, so whichever URL
 * lands in whichever variable decides what an editor's save rebuilds.
 */
export type RebuildTargetKey = "develop" | "production";

export interface RebuildTarget {
    key: RebuildTargetKey;
    /** Shown to the editor, so it must name the environment in their words. */
    label: string;
    url: string;
}

/**
 * Read at module scope because Vite inlines `import.meta.env` at build time —
 * these are not runtime lookups, and changing a hook means rebuilding and
 * redeploying the CMS.
 */
const configured: Record<RebuildTargetKey, string | undefined> = {
    develop: import.meta.env.VITE_DEPLOY_HOOK_DEVELOP,
    production: import.meta.env.VITE_DEPLOY_HOOK_PRODUCTION,
};

const labels: Record<RebuildTargetKey, string> = {
    develop: "develop",
    production: "production",
};

/**
 * Only the environments this CMS build actually knows about.
 *
 * **An unset hook is skipped, never POSTed as `undefined`.** That is what lets
 * develop ship before production's hook exists, and what keeps `pnpm dev:cms`
 * working on a machine with neither set — a local editor saving a record should
 * not fire a real deploy, and should not see an error either.
 */
export const rebuildTargets: RebuildTarget[] = (
    Object.keys(configured) as RebuildTargetKey[]
)
    .map((key) => ({ key, label: labels[key], url: configured[key]?.trim() }))
    .filter((target): target is RebuildTarget => Boolean(target.url));

export type RebuildOutcome =
    | { key: RebuildTargetKey; label: string; ok: true }
    | { key: RebuildTargetKey; label: string; ok: false; reason: string };

/**
 * POST one deploy hook.
 *
 * **`mode: "no-cors"`, and that is a real limitation rather than a shortcut.**
 * Cloudflare's deploy-hook endpoint sends no `Access-Control-Allow-Origin` on
 * either the preflight or the POST (verified 2026-09-03), so a normal `cors`
 * fetch from the CMS origin is blocked before the request leaves the browser.
 * `no-cors` does send it — a bodyless, header-free POST is a "simple request",
 * so there is no preflight to fail — and Cloudflare starts the build.
 *
 * The cost is that the response is opaque: `status` is 0 and unreadable. So a
 * rejected promise (offline, DNS failure, a blocking extension) is reported,
 * but an HTTP-level failure — a revoked or deleted hook answering 404 — is
 * indistinguishable from success. **Do not add a header or a JSON body here:**
 * either one makes this a preflighted request, which CORS then blocks, and the
 * build silently stops happening at all.
 *
 * Closing that gap properly means proxying through a Pages Function on the
 * CMS's own origin, which would also stop the hook URL being public — see the
 * note in `.env.example`.
 */
async function post(target: RebuildTarget): Promise<RebuildOutcome> {
    try {
        await fetch(target.url, { method: "POST", mode: "no-cors" });
        return { key: target.key, label: target.label, ok: true };
    } catch (error) {
        return {
            key: target.key,
            label: target.label,
            ok: false,
            reason: error instanceof Error ? error.message : String(error),
        };
    }
}

/**
 * Fire every configured hook, in parallel, and report each one separately.
 *
 * Per-environment results rather than one boolean: "a rebuild failed" is
 * unactionable when there are two of them, and the editor needs to know whether
 * the copy they are about to review on develop is the copy they just saved.
 */
export async function triggerRebuilds(
    targets: RebuildTarget[] = rebuildTargets,
): Promise<RebuildOutcome[]> {
    return Promise.all(targets.map(post));
}

/**
 * What to show an editor for a set of outcomes, or `null` to stay quiet.
 *
 * Takes the strings rather than reading a locale itself, so it stays a pure
 * function of its inputs and the caller — React component or plain callback —
 * decides how the locale is resolved.
 */
export function describeOutcomes(
    outcomes: RebuildOutcome[],
    strings: CollectionStrings["rebuild"],
): {
    type: "success" | "warning" | "error";
    message: string;
} | null {
    if (outcomes.length === 0) return null;

    const failed = outcomes.filter((outcome) => !outcome.ok);
    const names = (list: RebuildOutcome[]) =>
        list.map((outcome) => outcome.label).join(" and ");
    const rebuilds = (list: RebuildOutcome[]) =>
        `${names(list)} rebuild${list.length > 1 ? "s" : ""}`;

    if (failed.length === 0) {
        return { type: "success", message: strings.triggered(names(outcomes)) };
    }

    if (failed.length === outcomes.length) {
        return { type: "error", message: strings.allFailed(rebuilds(failed)) };
    }

    return {
        type: "warning",
        message: strings.partial(
            names(outcomes.filter((o) => o.ok)),
            rebuilds(failed),
        ),
    };
}
